"use client";

import { useTranslations } from "next-intl";
import { StarRating } from "@/components/ui/star-rating";
import { Link } from "@/i18n/navigation";
import { useDateFormatter } from "@/lib/i18n/date";
import { cn } from "@/lib/utils";
import type { FeedbackResponseDto } from "@/types/feedback";
import { FeedbackStatusBadge } from "./FeedbackStatusBadge";
import { FeedbackTypeBadge } from "./FeedbackTypeBadge";

interface FeedbackCardProps {
  feedback: FeedbackResponseDto;
  className?: string;
}

export const FeedbackCard: React.FC<FeedbackCardProps> = ({
  feedback,
  className,
}) => {
  const t = useTranslations("feedback");
  const df = useDateFormatter();
  const truncatedMessage =
    feedback.message.length > 100
      ? `${feedback.message.slice(0, 100)}...`
      : feedback.message;

  const formattedDate = df.numeric(feedback.created_at);

  return (
    <Link href={`/profile/feedback/${feedback.id}`}>
      <div
        className={cn(
          "bg-bg-secondary rounded-xl p-4 space-y-2 border border-transparent transition-all duration-100",
          "hover:border-stroke-light cursor-pointer active:scale-[0.98]",
          className,
        )}
      >
        <StarRating value={feedback.rating} readonly size="sm" />

        <div className="flex flex-wrap gap-2 items-center">
          <FeedbackTypeBadge type={feedback.type} />
          <span className="text-xs text-text-secondary">
            {t(`department.${feedback.department}`)}
          </span>
          <FeedbackStatusBadge status={feedback.status} />
        </div>

        <p className="text-sm text-text-secondary leading-normal">
          {truncatedMessage}
        </p>

        <p className="text-xs text-text-secondary">{formattedDate}</p>
      </div>
    </Link>
  );
};
